import React, {
  useLayoutEffect,
  useRef,
  useState,
  memo,
  forwardRef,
  useImperativeHandle
} from "react";
import PropTypes from "kf-proptypes/widgets";

import { useWindowSize } from "base/hooks";

import styles from "./horizontalscroller.css";

export const HorizontalScrollerComponent = forwardRef(
  (
    {
      leftNavRenderer,
      rightNavRenderer,
      children,
      className,
      containerClassName,
      disableWheelScroll = false
    },
    ref
  ) => {
    const windowSize = useWindowSize();
    const scrollerRef = useRef();

    const [navVisible, setNavVisible] = useState(false);

    const [leftNavDisabled, setLeftNavDisabled] = useState(true);
    const [rightNavDisabled, setRightNavDisabled] = useState(false);

    useImperativeHandle(ref, () => {
      return {
        scrollParent: scrollerRef.current
      };
    });

    function onLeftNav() {
      scrollerRef.current.scrollTo({
        top: 0,
        left:
          scrollerRef.current.scrollLeft -
          (window.isRTL ? -1 : 1) * scrollerRef.current.offsetWidth,
        behavior: "smooth"
      });
    }

    function onRightNav() {
      scrollerRef.current.scrollTo({
        top: 0,
        left:
          scrollerRef.current.scrollLeft +
          (window.isRTL ? -1 : 1) * scrollerRef.current.offsetWidth,
        behavior: "smooth"
      });
    }

    useLayoutEffect(function init() {
      scrollerRef.current.addEventListener("scroll", (e) => {
        if (Math.abs(scrollerRef.current.scrollLeft) <= 2) {
          setLeftNavDisabled(true);
        } else {
          setLeftNavDisabled(false);
        }
        if (
          scrollerRef.current.scrollWidth <=
          Math.abs(scrollerRef.current.scrollLeft) +
            scrollerRef.current.offsetWidth +
            2
        ) {
          setRightNavDisabled(true);
        } else {
          setRightNavDisabled(false);
        }
      });
      !disableWheelScroll &&
        scrollerRef.current.addEventListener("wheel", (e) => {
          e.preventDefault();
          scrollerRef.current.scrollLeft +=
            Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAnimationFrame = () => {
      if (
        scrollerRef.current &&
        scrollerRef.current.scrollWidth >
          scrollerRef.current.parentElement.offsetWidth
      ) {
        setNavVisible(true);
      } else {
        setNavVisible(false);
      }
    };

    useLayoutEffect(
      function showhideNav() {
        requestAnimationFrame(() => {
          setTimeout(onAnimationFrame, 100);
        });
      },
      [windowSize.width, children]
    );

    return (
      <div className={`${styles.container} ${containerClassName}`}>
        {leftNavRenderer &&
          leftNavRenderer({
            disabled: leftNavDisabled,
            visible: navVisible,
            onNav: onLeftNav
          })}
        <div className={`${styles.scroller} ${className}`} ref={scrollerRef}>
          {children}
        </div>
        {rightNavRenderer &&
          rightNavRenderer({
            disabled: rightNavDisabled,
            visible: navVisible,
            onNav: onRightNav
          })}
      </div>
    );
  }
);

HorizontalScrollerComponent.propTypes = {
  /** A render props callback function to render the left navbar of the horizontal scroll component. */
  leftNavRenderer: PropTypes.func.isRequired,
  /** A render props callback function to render the right navbar of the horizontal scroll component. */
  rightNavRenderer: PropTypes.func.isRequired,
  /** A custom render to display as children for this prop */
  children: PropTypes.node.isRequired,
  /** Overrides the default behaviour of the component */
  className: PropTypes.string,
  /** Overrides the default behaviour of the container */
  containerClassName: PropTypes.string,
  /** Disable the horizontal scroll.   */
  disableWheelScroll: PropTypes.bool
};

HorizontalScrollerComponent.defaultProps = {
  disableWheelScroll: false
};
export const HorizontalScroller = memo(HorizontalScrollerComponent);
